/**
 * @fileOverview Show transaction details view inline of activity module
 * @name TransactionDetailsView
 * @author tsullivan
 */

import Backbone from 'backbone';
import _ from 'underscore';
import tooltip from 'widgets/thetooltip';
import SharedPanel from 'view/activity/sharedPanel';
import {createLinkTracker} from 'lib/fpti-utils';

export default SharedPanel.extend({
	template: 'txndetails/modules',

	events () {
		return _.extend({}, SharedPanel.prototype.events, {
			'click .js_printTd': 'printTd',
			'click .refetch-transaction': 'retryFetch',

			// tooltips
			'click .js_gspViewBuyerAddress': 'triggerFullScreenTooltip',
			'click .js_posReturnPolicy': 'triggerFullScreenTooltip',

			// Request money actions.
			'click .js_reqMoneyCancelAction': 'cancelReqMoney',
			'click .js_reqMoneyEditSubmit': 'editReqMoney',

			// payment actions
			'click .js_accept': 'paymentActions',
			'click .js_deny': 'paymentActions',
			'click .js_cancel': 'paymentActions',

			'click .js_boletoCancel': 'cancelBoleto',
			'click .js_accept-add-bank': 'redirectToAddbank',

			// Pay now
			'click .js_payNow': 'paymentActions'
		});
	},

	eventPrefix: 'transactionItem',

	/*
	 * initializes the view's models
	 */
	initialize (config) {

		this.viewContext = config.viewContext;

		// render when the model data is done being fetched
		this.listenTo(this.model, 'sync', this.render);
		this.listenTo(this.model, 'error', this.handleError);
		this.listenTo(this.model, 'change:requestedToShow', this.showHideWithPossiblePreliminaryFetch);
		this.listenTo(this.model, 'change:isRendered', this.triggerFPTIOnOpen);
	},

	/*
	 * Handles request money cancel action
	 */
	cancelReqMoney () {
		let action = this.model.get('txnAction'),
			self = this;

		Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), true);

		function success(response) {
			if (action) {
				action.isSuccessful = true;
				action.state = 'complete';
				self.model.set('shouldRefresh', true); // This is used to refresh the page, so that user sees updated data.
			}
			Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
			self.render();
		}

		function error(response) {
			if (action) {
				action.isSuccessful = false;
				action.state = 'complete';
			}
			Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
			self.render();
		}

		Backbone.ajax({
			type: 'POST',
			url: Backbone.history.options.root + 'transaction/request/cancel/' + this.model.get('id') + '/' + this.model.get('groupRequestId'),
			success: success,
			error: error,
			ignoreErrors: true
		});
	},

	/*
	 * Handles request money edit action
	 */
	editReqMoney (event) {
		let action = this.model.get('txnAction'),
			inputField = this.$('.js_reqMoneyEditInput'),
			editAmount = inputField.val(),
			currencyCode = inputField.data('currencyCode'),
			self = this;

		function success(response) {
			const txnAction = response && response.txnAction;

			if (action) {
				action.state = txnAction && txnAction.state || 'complete';
				action.isSuccessful = txnAction && txnAction.isSuccessful || false;
				action.formattedAmount = txnAction && txnAction.formattedAmount || editAmount;
				action.invalidAmount = txnAction && txnAction.invalidAmount || false;
				self.model.set('shouldRefresh', true); // This is used to refresh the page, so that user sees updated data.
			}
			Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
			self.render();
		}

		function error(response) {
			if (action) {
				action.isSuccessful = false;
			}
			Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
			self.render();
		}

		Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), true);
		action.state = 'complete';
		action.editAmount = editAmount;

		Backbone.ajax({
			type: 'PATCH',
			url: Backbone.history.options.root + 'transaction/request/edit/' + this.model.get('id') + '/' + this.model.get('groupRequestId'),
			data: {
				amount: editAmount,
				currencyCode: currencyCode
			},
			success: success,
			error: error,
			ignoreErrors: true
		});
	},

	/*
	 * Handles accept/deny action
	 */
	cancelBoleto (event) {
		let action = this.model.get('txnAction'),
			self = this;

		Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), true);

		function success(response) {
			if (action) {
				action.isSuccessful = response.data && response.data.action && response.data.action.isSuccessful;
				action.state = 'complete';
				self.model.set('shouldRefresh', true); // This is used to refresh the page, so that user sees updated data.
				self.triggerFPTI('cancelBoleto');
			}
			Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
			self.render();
		}

		function error(response) {
			if (action) {
				action.isSuccessful = false;
				action.state = 'complete';
				self.triggerFPTI('cancelBoleto:error');
			}
			Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
			self.render();
		}

		Backbone.sync('create', this.model, {
			url: Backbone.history.options.root + 'transaction/boleto/cancel/' + this.model.get('id'),
			success: success,
			error: error
		});
	},

	/*
	 * Handles accept/deny action
	 */
	paymentActions (event) {
		let action = this.model.get('txnAction'),
			currencyCode = this.$(event.target).data('currencyCode'),
			self = this,
			eventName,
			actionName;

		if (action) {
			actionName = action.selectedAction;
		}

		if (!actionName) {
			action.isSuccessful = false;
			action.state = 'complete';
			return this.render();
		}

		eventName = actionName;
		if (actionName === 'accept' && action.data) {
			eventName = eventName + ':' + action.data.holdReason;

			if (action.data.holdReason === 'cross_currency') {
				if (action.data.primaryCurrency === currencyCode) {
					eventName = eventName + ':' + 'convert'; // accept:cross_currency:convert
				} else {
					eventName = eventName + ':' + 'keep'; // accept:cross_currency:keep
				}
			}
		}

		Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), true);

		function success(response) {
			if (action) {
				action.isSuccessful = response.data && response.data.action && response.data.action.isSuccessful;
				action.state = 'complete';
				self.model.set('shouldRefresh', true); // This is used to refresh the page, so that user sees updated data.
				self.triggerFPTI(eventName);
			}
			Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
			self.render();
		}

		function error(response) {
			if (action) {
				action.isSuccessful = false;
				action.state = 'complete';
				self.triggerFPTI(eventName + ':error');
			}
			Backbone.trigger('transactionItem:toggleLoading', self.model.get('id'), false);
			self.render();
		}

		Backbone.sync('create', this.model, {
			url: Backbone.history.options.root + 'transaction/' + actionName + '/' + this.model.get('id'),
			attrs: { currencyCode: currencyCode},
			success: success,
			error: error
		});
	},

	redirectToAddbank () {
		 window.open('/myaccount/wallet/add/bank', '_self');
	},

	triggerRender () {
		// fetches the data model and renders the transaction details template
		this.model.set('inline', true);
		this.model.fetch();
	},

	/*
	 * If inline, then open a window to fullpage TD and trigger print dialogue
	 * Otherwise just trigger print dialog
	 */
	printTd (event) {
		event.preventDefault();

		if (this.model.get('inline')) {
			// open print-friendly full-page in a new tab
			const printDetailsLink = this.$('.js_printTd').attr('href');
			if (printDetailsLink) {
				window.open(printDetailsLink, '_tdPrint');
			} else {
				window.open(
					'/myaccount/transaction/print-details/' + this.model.get('id'),
					'_tdPrint');
			}
		} else {
			// already full-page, just do print
			window.print();
		}
	},

	/*
	 * Trigger a hidden fullScreenTooltip with a link
	 *
	 * The link requires a data-tooltipid attribute that selects the
	 * fullScreenTooltip widget.
	 *
	 * This allows the fullScreenTooltip widget to be included anywhere on
	 * the page, separate from the link that activates it, and avoids
	 * breaking markup.
	 */
	triggerFullScreenTooltip (event) {
		const elData = this.$(event.target) && this.$(event.target).data(),
			tooltipContentSelector = elData && elData.tooltipid;

		event.preventDefault();

		if (tooltipContentSelector) {
			// sets the content element on the tooltip object and shows the tooltip
			tooltip.showContentFromSelector(tooltipContentSelector);
		}
	},

	/*
	 * if the back-end returns invalid txn details data, show error content
	 */
	handleError (model, status, message) {
		this.model.set({
			error: {

				message: message
			}
		});
		this.render();
		this.triggerFPTI('td_open:error');
	},

	/*
	 * user-invoked retry method in case an error message was shown
	 */
	retryFetch (event) {
		event.preventDefault();
		Backbone.trigger(this.eventPrefix + ':toggleLoading', this.model.get('id'));
		this.triggerFPTI('td_open:error:reloadlink');
		this.model.fetch();
	},

	/*
	 * method to fire FPTI tracking
	 */
	triggerFPTI (eventName) {

		this.fptiTxnType = this.fptiTxnType ||
			this.$el.children('.inlineTransactionDetails').data('fptitag');

		// create method if it does not already exist--so this should only
		// run createLinkTracker once. we want to wait until after render
		// to create this method so we can get the 'fptitag' for txntype
		// above; otherwise, we'd put it in the initializer.
		this.trackFPTILink = this.trackFPTILink || createLinkTracker([
			'main:walletweb',
			this.viewContext || 'activity',
			this.fptiTxnType
		].join(':'));

		this.trackFPTILink(eventName, this.fptiTxnType);
	},

	/*
	 * method to fire FPTI tracking event for viewing txndetails inline
	 */
	triggerFPTIOnOpen (model) {
		if (model.get('isRendered')) {
			this.triggerFPTI('td_open');
		}
	}
});



// WEBPACK FOOTER //
// ./view/txndetails/modules.es6